import React, { useState, useRef, KeyboardEvent, useEffect } from "react";
import { Input, Icon } from "semantic-ui-react";
import { useTranslation } from "react-i18next";

interface Props {
  iframeRef: React.RefObject<HTMLIFrameElement>;
   resetTrigger?: any; 
}

export const SmartSearch = ({ iframeRef, resetTrigger }: Props) => {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<HTMLElement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef<Input>(null);
  const { t } = useTranslation();

  const injectStyles = (doc: Document) => {
    if (doc.getElementById("smart-search-style")) return;
    const style = doc.createElement("style");
    style.id = "smart-search-style";
    style.innerHTML = `
      mark.smart-search-highlight {
        background: yellow;
        border-radius: 2px;
      }
      mark.smart-search-highlight.active {
        background: rgb(136 0 21);
        color: white;
      }
    `;
    doc.head.appendChild(style);
  };

  useEffect(() => {
    setQuery("");
    setMatches([]);
    setCurrentIndex(0);
    const doc = iframeRef.current?.contentDocument;
    if (doc) clearHighlights(doc);
  }, [resetTrigger]);

  const escapeRegExp = (str: string) =>
    str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const getFlattenedTextNodes = (root: HTMLElement) => {
    const nodes: { node: Text; start: number; end: number }[] = [];
    let position = 0;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent || parent.offsetParent === null)
          return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    while (walker.nextNode()) {
      const node = walker.currentNode as Text;
      const length = node.nodeValue?.length || 0;
      nodes.push({ node, start: position, end: position + length });
      position += length;
    }

    return { nodes, totalText: nodes.map((n) => n.node.nodeValue).join("") };
  };

  const clearHighlights = (doc: Document) => {
    doc.querySelectorAll("mark.smart-search-highlight").forEach((el) => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent || ""), el);
        parent.normalize();
      }
    });
  };

  const highlightAllMatches = (
    doc: Document,
    searchTerm: string
  ): HTMLElement[] => {
    const highlights: HTMLElement[] = [];

    clearHighlights(doc);

    if (!searchTerm || searchTerm.length < 2) return highlights;

    let { nodes, totalText } = getFlattenedTextNodes(doc.body);
    const safeSearchTerm = escapeRegExp(searchTerm);
    const regex = new RegExp(safeSearchTerm, "gi");
    let match: RegExpExecArray | null;

    while ((match = regex.exec(totalText)) !== null) {
      const matchStart = match.index;
      const matchEnd = regex.lastIndex;

      let startNodeInfo = nodes.find(
        (n) => matchStart >= n.start && matchStart < n.end
      );
      let endNodeInfo = nodes.find(
        (n) => matchEnd > n.start && matchEnd <= n.end
      );

      if (!endNodeInfo) endNodeInfo = nodes[nodes.length - 1];

      if (startNodeInfo && endNodeInfo) {
        const range = doc.createRange();
        const startOffset = Math.min(
          matchStart - startNodeInfo.start,
          startNodeInfo.node.length
        );
        const endOffset = Math.min(
          matchEnd - endNodeInfo.start,
          endNodeInfo.node.length
        );

        try {
          range.setStart(startNodeInfo.node, startOffset);
          range.setEnd(endNodeInfo.node, endOffset);

          const mark = doc.createElement("mark");
          mark.className = "smart-search-highlight";
          mark.textContent = range.toString();

          range.deleteContents();
          range.insertNode(mark);
          highlights.push(mark);

          const updated = getFlattenedTextNodes(doc.body);
          nodes.length = 0;
          nodes.push(...updated.nodes);
          totalText = updated.totalText;

          regex.lastIndex = matchStart + mark.textContent.length;
        } catch (e) {
          console.error("Highlighting error", e);
        }
      }
    }

    return highlights;
  };

  const scrollToMatch = (el: HTMLElement) => {
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const updateActiveMatch = (newIndex: number) => {
    matches.forEach((el) => el.classList.remove("active"));
    const active = matches[newIndex];
    if (active) {
      active.classList.add("active");
      scrollToMatch(active);
      setCurrentIndex(newIndex);
    }
  };

  const handleSearch = (val: string) => {
    setQuery(val);
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument || iframe?.contentWindow?.document;
    if (!doc) return;

    injectStyles(doc);
    const found = highlightAllMatches(doc, val);
    setMatches(found);
    setCurrentIndex(0);
    if (found.length > 0) updateActiveMatch(0);
  };

  const handleKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      moveSelection("down");
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      moveSelection("up");
    }
  };

  const moveSelection = (direction: "up" | "down") => {
    if (matches.length === 0) return;
    const newIndex =
      direction === "down"
        ? (currentIndex + 1) % matches.length
        : (currentIndex - 1 + matches.length) % matches.length;
    updateActiveMatch(newIndex);
  };

  return (
    <Input
      ref={inputRef}
      size="mini"
      placeholder={t("search")}
      value={query}
      onChange={(e) => handleSearch(e.target.value)}
      onKeyDown={handleKey}
      style={{ margin: "0 10px", maxWidth:"40%"
       }}
      labelPosition="right"
      label={{
        basic: true,
        content:
          matches.length > 0 ? (
            <>
            <span style={{marginRight: "10px", color: "grey"}}>
              {currentIndex + 1} / {matches.length}
            </span>
            <Icon
              name="arrow up"
              style={{ cursor: "pointer"}}
              onClick={() => {
                moveSelection("up");
              }} />
              <Icon
              name="arrow down"
              style={{ cursor: "pointer"}}
              onClick={() => {
                moveSelection("down");
              }} />
              {/* <Icon
              name="x"
              style={{ cursor: "pointer", padding: "0 4px" }}
              onClick={() => {
                setQuery("");
                setMatches([]);
                setCurrentIndex(0);
                const doc = iframeRef.current?.contentDocument;
                if (doc) clearHighlights(doc);
              }} /> */}
            </>
          ) : (
            <Icon name="search" color="grey" style={{marginLeft: "5px"}} />
          ),
      }}
    />
  );
};
